import { useEffect, useRef, useState } from "react";
import "./navigation.scss";
import { useTranslation } from "react-i18next";
import { Paragraph, I18NHeading } from "../../Elements";
import { NavigationEntry } from "./NavigationEntry";
import i18next from "i18next";
import { UntitledColorizeIcon, UntitledIcon } from "../../Basic/icons";
import { Button } from "../../Controls";
import { Spacing } from "../../Redesign/Spacing";
import { useNavigate } from "react-router-dom";
import type { NavigationMenuProps, NavigationProps } from "./Navigation.interface";

function NavigationMenu({ userPrincipalName, username, toggleCollapse, collapseVisible, entries }: NavigationMenuProps) {
	const { t } = useTranslation();

	const [, setCurrentLang] = useState("de");

	const sidebarRef = useRef<HTMLDivElement>(null);

	useEffect(() => {

		function handleClickOutside(event: MouseEvent) {
			if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node) && collapseVisible) {
				toggleCollapse();
			}
		}

		document.addEventListener("mousedown", handleClickOutside);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [collapseVisible, toggleCollapse]);

	function changeLanguage() {

		const lang = i18next.resolvedLanguage?.includes("de") ? "en" : "de";

		i18next.changeLanguage(lang, (err) => {
			setCurrentLang(lang);
			if (err) return console.log("[CS] something went wrong loading", err);
		});
	}

	function UpperInformation() {
		if (collapseVisible) {
			// in open state
			if (username && userPrincipalName) {
				return (
					<div className="navigation-welcome">
						<Paragraph className="strong name" text={username} />
						<Paragraph className="mail" text={userPrincipalName} />
					</div>
				);
			} else {
				return (
					<div className={"navigation-welcome"}>
						<I18NHeading type={"h4"} i18n={"navigation.welcome"} />
					</div>
				);
			}
		} else {
			// in close state
			return (<div className={"navigation-menu-heading"} />);
		}
	}

	function CombineMenuEntries() {

		return (
			<>
				<NavigationEntry
					id={"navigation-language"}
					icon={"globe-01"}
					i18nLabel={t("navigation.changeLang.label")}
					onClick={changeLanguage}
				/>
				{entries.map((entry) => {
					if (entry.id === "navigation-logout") {
						return (
							<Button
								id={entry.id}
								key={entry.id}
								onClick={entry.onClick}
								label={t(entry.i18nLabel)}
							/>
						);
					}

					return (
						<NavigationEntry
							key={entry.id}
							id={entry.id}
							i18nLabel={entry.i18nLabel}
							icon={entry.icon}
							onClick={() => {
								if (entry.onClick) {
									entry.onClick();
									setTimeout(toggleCollapse, 50); // to avoid flickering
								}
							}}
							i18nNote={entry.i18nNote}
						/>
					);
				})}
			</>
		);
	}

	return (<div className="navigation-wrapper" ref={sidebarRef}>
		<div className="navigation-header" onClick={toggleCollapse}>
			{collapseVisible ? (
				<UntitledColorizeIcon
					id="loginCollapse"
					className="toggle-container"
					style={{ marginRight: "16px", height: "50px" }}
					name="cross"
				/>
			) : (
				<UntitledColorizeIcon
					id="loginCollapse"
					className="toggle-container"
					name="burger-menu"
				/>
			)}
		</div>
		<>
			<UpperInformation />
			{collapseVisible && (
				<div className="navigation-content"><CombineMenuEntries /></div>
			)}
		</>
	</div>);
}

/**
 * Primary UI component for user interaction
 */
export function Navigation({ username, userPrincipalName, entries, subpage, title }:NavigationProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [collapseContentVisible, setCollapseContentVisible] = useState(false);

	function toggleCollapse (){
		if (collapseContentVisible) {
			setCollapseContentVisible(false);
		} else {
			setCollapseContentVisible(true);
		}
	}

	return (
		<nav role="navigation" aria-label={"Navigation"} className={collapseContentVisible ? "active navigation" : ""}>
			<Spacing fullWidthChildren={false} alignItems="center" gap="16">
				<button className="storybook-icon-button navigation-header-button" onClick={() => navigate("/cp/fidoCredentials")} style={{ cursor: "pointer" }}>
					{subpage ? (
						<UntitledColorizeIcon
							style={{
								fill: "var(--button-label-color)",
								color: "var(--button-label-color)",
							}}
							name="real-arrow"
							role="img"
						/>
					) : (
						<UntitledIcon name="logo" viewBox="0 0 32 16" />)}
					{title && (
						<span className="navigation-title">{t(title)}</span>
					)}
				</button>
			</Spacing>
			<NavigationMenu userPrincipalName={userPrincipalName} username={username}
											toggleCollapse={toggleCollapse}
											collapseVisible={collapseContentVisible}
											entries={entries}
			/>
		</nav>
	);
}
